import { useCallback, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'
import { reportScan } from './scanTelemetry'

export type CheckInState = 'idle' | 'loading' | 'success' | 'already_in' | 'error'

export interface CheckInResult {
  member_name: string
  entries_remaining: number | null
}

interface PerUserState {
  state: CheckInState
  result: CheckInResult | null
  error: string | null
}

const DEFAULT_STATE: PerUserState = { state: 'idle', result: null, error: null }

export function useCheckIn() {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [stateMap, setStateMap] = useState<Map<string, PerUserState>>(new Map())

  function getEntry(key: string): PerUserState {
    return stateMap.get(key) ?? DEFAULT_STATE
  }

  const checkIn = useCallback(
    async (userId: string, childId?: string) => {
      const key = childId ? `${userId}:${childId}` : userId
      setStateMap((prev) => new Map(prev).set(key, { state: 'loading', result: null, error: null }))

      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/check-in`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            ...(childId ? { child_id: childId } : {}),
          }),
        })

        const data = (await res.json()) as {
          error?: string
          code?: string
          member_name?: string
          entries_remaining?: number | null
        }

        if (!res.ok) {
          reportScan('check-in request was refused', {
            stage: 'check-in',
            level: data.code === 'ALREADY_CHECKED_IN' ? 'info' : 'warning',
            data: { status: res.status, code: data.code ?? null, error: data.error ?? null },
          })
          if (data.code === 'ALREADY_CHECKED_IN') {
            setStateMap((prev) =>
              new Map(prev).set(key, {
                state: 'already_in',
                result: { member_name: data.member_name ?? 'Member', entries_remaining: null },
                error: null,
              }),
            )
            return
          }
          setStateMap((prev) =>
            new Map(prev).set(key, { state: 'error', result: null, error: data.error ?? null }),
          )
          return
        }

        setStateMap((prev) =>
          new Map(prev).set(key, {
            state: 'success',
            result: {
              member_name: data.member_name ?? 'Member',
              entries_remaining: data.entries_remaining ?? null,
            },
            error: null,
          }),
        )
      } catch (error) {
        // This used to swallow the exception whole, so a check-in that never
        // left the building looked identical in Sentry to one that did.
        reportScan('check-in request threw', { stage: 'check-in', level: 'error', error })
        setStateMap((prev) =>
          new Map(prev).set(key, { state: 'error', result: null, error: 'Network error.' }),
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAccessTokenSilently],
  )

  function stateFor(userId: string, childId?: string): CheckInState {
    const key = childId ? `${userId}:${childId}` : userId
    return getEntry(key).state
  }

  function resultFor(userId: string, childId?: string): CheckInResult | null {
    const key = childId ? `${userId}:${childId}` : userId
    return getEntry(key).result
  }

  function errorFor(userId: string, childId?: string): string | null {
    const key = childId ? `${userId}:${childId}` : userId
    return getEntry(key).error
  }

  function reset(userId: string, childId?: string) {
    const key = childId ? `${userId}:${childId}` : userId
    setStateMap((prev) => {
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }

  return { checkIn, stateFor, resultFor, errorFor, reset }
}
